import { useNavigate } from "react-router-dom";
import {
    ShoppingBag, Globe2, Layers, Info, Lock,
    TrendingUp, ChevronRight, ClipboardCheck,
    Clock, CheckCircle2,
    XCircle, Timer, AlertCircle, Copy, // ✅ Tambahan icon Copy
    RefreshCw, Loader2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import WelcomeCard from "@/pages/private/dashboard/WelcomeCard";
import { Card, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { PaymentFeeNotice } from "@/components/private/payment/PaymentFeeNotice";
import { motion } from "framer-motion";
import { useActivePeriod } from "@/hooks/useActivePeriod";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { useMemo, useState, useEffect, useRef, type ReactNode } from "react";
import { useUser } from "@/context/useUser";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { axiosClient } from "@/lib/axiosClient";
import { isAxiosError } from "axios";
import toast from "react-hot-toast"; // ✅ Tambahan Toast untuk notifikasi Copy

// 1. Buat Interface untuk struktur data Feature
interface DashboardFeature {
    key: string;
    title: string;
    description: string;
    icon: ReactNode;
    action: () => void;
    color: "blue" | "emerald" | "indigo" | "sky" | "amber";
    size: "large" | "medium" | "small";
    badge?: string; // Opsional, hanya ada di fitur utama
}

// 2. Interface untuk data pembayaran
interface PaymentData {
    payment_id: number;
    registration_id: number;
    amount: number;
    status: "unpaid" | "pending" | "paid" | "expired";
    gateway_transaction_id: string;
    invoice_number: string;
    description: string;
    package_name?: string; // ✅ Tambahan field untuk nama paket
    created_at: string;
    expired_at_payment: string;
    updated_at: string;
}

export default function DashboardPage() {
    const { t } = useTranslation('private');
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { data: activePeriod, isLoading: periodLoading } = useActivePeriod();

    // Ambil data user menggunakan hook useUser
    const { data: user, isLoading: userLoading } = useUser();

    // Hanya Administrator (1), Pendaftar (2), dan Pendaftar Kolektif (3) yang
    // benar-benar punya keperluan memilih paket/melihat status pembayaran.
    // Whitelist (bukan blacklist) supaya role baru yang ditambahkan nanti
    // otomatis tidak melihat info ini kecuali sengaja ditambahkan ke daftar.
    const showPaymentInfo = Boolean(user) && [1, 2, 3].includes(user!.role_id);

    // Fetch payment data
    const { data: payment, isLoading: paymentLoading } = useQuery({
        queryKey: ["payment", user?.participant_id, activePeriod?.id],
        queryFn: async () => {
            if (!user?.participant_id || !activePeriod?.id) return null;
            try {
                const { data } = await axiosClient.get<{ data: PaymentData }>(
                    `/participants/${user.participant_id}/registrations/period/${activePeriod.id}/payment`
                );
                return data.data;
            } catch {
                return null;
            }
        },
        enabled: Boolean(showPaymentInfo && user?.participant_id && activePeriod?.id),
        staleTime: 1000 * 60 * 5,
        gcTime: 1000 * 60 * 10,
    });

    // Cek ulang status pembayaran ke Payment Gateway - dipakai baik oleh tombol manual
    // maupun auto-check sekali saat halaman dibuka (lihat useEffect di bawah), untuk
    // menangkap pembayaran yang baru masuk tapi belum sempat ditangkap webhook bank.
    const isAutoCheckRef = useRef(false);
    const checkPaymentStatusMutation = useMutation({
        mutationFn: async () => {
            if (!payment?.payment_id) throw new Error("Data tagihan tidak ditemukan.");
            const { data } = await axiosClient.get<{ status?: string; message?: string }>(
                `/payments/sync-status/${payment.payment_id}`
            );
            return data;
        },
        onSuccess: (data) => {
            const newStatus = data.status?.toLowerCase();
            const wasAutoCheck = isAutoCheckRef.current;
            isAutoCheckRef.current = false;

            if (newStatus === "paid") {
                toast.success(data.message || "Status berhasil disinkronkan: LUNAS!");
            } else if (!wasAutoCheck) {
                // Toast "belum berubah" hanya ditampilkan untuk pengecekan manual (klik tombol) -
                // auto-check di background tidak perlu mengganggu user tiap buka dashboard.
                toast(data.message || "Status pembayaran belum berubah (masih belum lunas).", { icon: 'ℹ️' });
            }
            queryClient.invalidateQueries({ queryKey: ["payment", user?.participant_id, activePeriod?.id] });
        },
        onError: (err: unknown) => {
            const wasAutoCheck = isAutoCheckRef.current;
            isAutoCheckRef.current = false;
            // Auto-check yang gagal (mis. gateway lagi down) cukup dicatat di console,
            // tidak perlu toast error mengagetkan user yang baru buka dashboard.
            if (wasAutoCheck) {
                console.error("Auto-check status pembayaran gagal:", err);
                return;
            }

            let errorMessage = "Gagal memeriksa status pembayaran ke server Payment Gateway.";
            if (isAxiosError(err) && err.response?.data?.message) {
                errorMessage = err.response.data.message;
            } else if (err instanceof Error) {
                errorMessage = err.message;
            }
            toast.error(errorMessage);
        },
    });

    // Auto-check SEKALI ke Payment Gateway saat halaman dibuka & tagihan masih belum lunas,
    // supaya user tidak perlu klik manual dulu untuk lihat status terbaru. Ref dipakai untuk
    // memastikan hanya jalan sekali per payment_id (tidak berulang tiap kali query refetch).
    const autoCheckedPaymentIdRef = useRef<number | null>(null);
    useEffect(() => {
        if (!payment?.payment_id || payment.status === "paid") return;
        if (autoCheckedPaymentIdRef.current === payment.payment_id) return;
        autoCheckedPaymentIdRef.current = payment.payment_id;
        isAutoCheckRef.current = true;
        checkPaymentStatusMutation.mutate();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [payment?.payment_id, payment?.status]);

    // LOGIKA TIMER
    const [timeLeft, setTimeLeft] = useState<string>("");

    useEffect(() => {
        if (!payment?.expired_at_payment || payment.status === "paid") return;

        const timer = setInterval(() => {
            const now = new Date().getTime();
            const target = new Date(payment.expired_at_payment).getTime();
            const distance = target - now;

            if (distance < 0) {
                setTimeLeft("KEDALUWARSA");
                clearInterval(timer);
                return;
            }

            const days = Math.floor(distance / (1000 * 60 * 60 * 24));
            const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
            const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
            const seconds = Math.floor((distance % (1000 * 60)) / 1000);

            if (days > 0) {
                setTimeLeft(`${days} hari ${hours}j ${minutes}m`);
            } else {
                setTimeLeft(`${hours}j ${minutes}m ${seconds}s`);
            }
        }, 1000);

        return () => clearInterval(timer);
    }, [payment?.expired_at_payment, payment?.status]);

    // Gabungkan loading state
    const isLoading = periodLoading || userLoading;
    const isOrderDisabled = !activePeriod;

    // Terapkan Interface ke dalam array features secara Dinamis
    const features: DashboardFeature[] = useMemo(() => {
        const items: DashboardFeature[] = [];

        // FITUR 1: ORDER PAKET 
        if ([1, 2, 3].includes(user?.role_id as number)) {
            items.push({
                key: "order",
                title: t('dashboard.features.order.title'),
                description: t('dashboard.features.order.desc'),
                badge: t('dashboard.features.order.badge'),
                icon: <ShoppingBag />,
                action: () => navigate("/order/paket"),
                color: "blue",
                size: "medium",
            });
        }

        // FITUR 2: VERIFIKASI CONFERENCE
        if ([1, 4].includes(user?.role_id as number)) {
            items.push({
                key: "verification",
                title: t('dashboard.features.verification.title'),
                description: t('dashboard.features.verification.desc'),
                badge: t('dashboard.features.verification.badge'),
                icon: <ClipboardCheck />,
                action: () => navigate("/conference-verification"),
                color: user?.role_id === 1 ? "emerald" : "blue",
                size: "medium",
            });
        }

        // FITUR 3: MENU UMUM
        items.push(
            {
                key: "tracks",
                title: t('dashboard.features.tracks.title'),
                description: t('dashboard.features.tracks.desc'),
                icon: <Layers />,
                action: () => navigate("/conference-tracks"),
                color: user?.role_id === 1 ? "indigo" : "emerald",
                size: "medium",
            },
            {
                key: "public",
                title: t('dashboard.features.public.title'),
                description: t('dashboard.features.public.desc'),
                icon: <Globe2 />,
                action: () => navigate("/"),
                color: user?.role_id === 1 ? "sky" : "indigo",
                size: "medium",
            },
            {
                key: "about",
                title: t('dashboard.features.about.title'),
                description: t('dashboard.features.about.desc'),
                icon: <Info />,
                action: () => navigate("/about"),
                color: user?.role_id === 1 ? "amber" : "sky",
                size: "medium",
            }
        );

        // AUTO-SIZING GRID LOGIC
        if (items.length === 5) {
            items[0].size = "medium";
            items[1].size = "medium";
            items[2].size = "small";
            items[3].size = "small";
            items[4].size = "small";
        } else {
            items.forEach(i => i.size = "medium");
        }

        return items;
    }, [t, navigate, user?.role_id]);

    return (
        <div className="max-w-7xl mx-auto space-y-10 pb-20 px-4 md:px-0">
            {/* 1. Welcome Section */}
            <WelcomeCard />

            {/* 1.5 PAYMENT STATUS CARD */}
            {!showPaymentInfo ? null : paymentLoading ? (
                <div className="h-48 bg-slate-100 dark:bg-slate-800 rounded-[2rem] animate-pulse" />
            ) : payment ? (
                <Card className="border-none shadow-2xl dark:shadow-none bg-white dark:bg-slate-950 overflow-hidden p-0 rounded-[2rem]">
                    <CardContent className="p-0">
                        <div className="flex flex-col lg:flex-row">

                            {/* ✅ PERBAIKAN: Lebar diubah dari lg:w-[40%] menjadi lg:w-[30%] */}
                            <div className={cn(
                                "w-full lg:w-[30%] p-8 lg:p-10 flex flex-col justify-between relative overflow-hidden bg-gradient-to-br shrink-0",
                                payment.status === "paid" ? "from-emerald-600 to-teal-700" :
                                    (payment.status === "expired" ? "from-red-500 to-rose-700" : "from-amber-500 to-orange-600")
                            )}>
                                <div className="absolute -top-10 -left-10 w-40 h-40 bg-white/10 rounded-full blur-3xl animate-pulse" />

                                <div className="relative z-10">
                                    <div className="bg-white/20 w-fit p-4 rounded-2xl backdrop-blur-md mb-6 ring-1 ring-white/30">
                                        {payment.status === "paid" ? <CheckCircle2 className="w-8 h-8 text-white" /> :
                                            payment.status === "expired" ? <XCircle className="w-8 h-8 text-white" /> :
                                                <Clock className="w-8 h-8 text-white" />}
                                    </div>
                                    <h2 className="text-2xl font-extrabold text-white leading-tight tracking-tight">
                                        {payment.status === "paid" ? "Pembayaran Berhasil" :
                                            payment.status === "expired" ? "Tagihan Kedaluwarsa" :
                                                "Menunggu Pembayaran"}
                                    </h2>
                                    <p className="text-white/80 text-sm mt-2 font-medium">
                                        {payment.status === "paid" ? "Transaksi telah diverifikasi sistem" :
                                            payment.status === "expired" ? "Waktu pembayaran telah habis" :
                                                "Selesaikan sebelum batas waktu"}
                                    </p>

                                    {(payment.status === "unpaid" || payment.status === "pending") && timeLeft && (
                                        <div className="mt-8 bg-black/10 backdrop-blur-sm rounded-xl p-3 border border-white/20 flex items-center gap-3 w-fit">
                                            <Timer className="w-4 h-4 text-white" />
                                            <div>
                                                <p className="text-[9px] font-bold text-white/60 uppercase tracking-widest">Sisa Waktu</p>
                                                <p className="text-white font-mono font-bold text-sm">{timeLeft}</p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* ✅ PERBAIKAN: Lebar otomatis menyesuaikan dari lg:w-[60%] menjadi lg:w-[70%] */}
                            <div className="w-full lg:w-[70%] p-8 lg:p-10 bg-white dark:bg-slate-900/40 flex flex-col justify-between">
                                <div className="mb-6">
                                    <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-[0.3em] mb-2">Total Tagihan</p>
                                    <h3 className="text-3xl lg:text-4xl font-black text-slate-900 dark:text-white tracking-tighter">
                                        {new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(payment.amount)}
                                    </h3>

                                    {/* INFO PRODUK DAN COPY VA */}
                                    <div className="mt-5 flex flex-col sm:flex-row gap-4 sm:gap-8">
                                        <div className="space-y-1">
                                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Produk & Layanan</p>
                                            <p className="text-sm font-semibold text-slate-700 dark:text-slate-300 max-w-[280px] truncate" title={payment.package_name || payment.description || "Paket Layanan"}>
                                                {payment.package_name || payment.description || "Paket Konvensi"}
                                            </p>
                                        </div>
                                        <div className="space-y-1">
                                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">VA / Invoice</p>
                                            <div className="flex items-center gap-2">
                                                <p className="font-mono text-sm font-semibold text-slate-700 dark:text-slate-300">{payment.invoice_number}</p>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigator.clipboard.writeText(payment.invoice_number);
                                                        toast.success("VA/Invoice berhasil disalin!");
                                                    }}
                                                    className="p-1.5 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 rounded-md transition-colors text-slate-500 dark:text-slate-400"
                                                    title="Salin VA/Invoice"
                                                >
                                                    <Copy className="w-3.5 h-3.5" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Kontainer Alert - selalu bertumpuk (pesan di atas, tombol aksi di bawah)
                                    supaya tidak pecah di lebar layar manapun, walau ada 2 tombol aksi. */}
                                <div className={cn(
                                    "p-5 rounded-2xl flex flex-col gap-4 border border-transparent ring-1 ring-inset ring-white/5 mt-auto",
                                    payment.status === "paid" ? "bg-emerald-50 dark:bg-emerald-500/5" :
                                        payment.status === "expired" ? "bg-red-50 dark:bg-red-500/5" : "bg-amber-50 dark:bg-amber-500/5"
                                )}>
                                    <div className="flex items-start gap-3">
                                        <AlertCircle className={cn(
                                            "w-5 h-5 shrink-0 mt-0.5",
                                            payment.status === "paid" ? "text-emerald-600 dark:text-emerald-400" :
                                                payment.status === "expired" ? "text-red-600 dark:text-red-400" : "text-amber-600 dark:text-amber-400"
                                        )} />
                                        <div className="space-y-1 min-w-0">
                                            <p className="text-[9px] font-bold uppercase tracking-wider text-slate-500 opacity-70">Pesan Sistem:</p>
                                            <p className="text-xs font-medium text-slate-600 dark:text-slate-400 leading-relaxed italic">
                                                {payment.status === "paid"
                                                    ? "Pembayaran Anda telah lunas. Anda dapat melanjutkan ke tahapan berikutnya."
                                                    : payment.status === "expired"
                                                        ? "Tagihan ini sudah kedaluwarsa. Silakan buat tagihan baru jika diperlukan."
                                                        : "Segera lakukan pembayaran sesuai nominal yang tertera sebelum batas waktu berakhir."}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Baris tombol aksi: berdampingan sama lebar (label sudah dipersingkat supaya muat) */}
                                    <div className="flex flex-row gap-2 pl-8">
                                        {payment.status !== "paid" && (
                                            <Button
                                                variant="outline"
                                                onClick={() => checkPaymentStatusMutation.mutate()}
                                                disabled={checkPaymentStatusMutation.isPending}
                                                className="flex-1 font-bold text-xs uppercase tracking-wider rounded-xl px-4 py-2.5 transition-all border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"
                                            >
                                                {checkPaymentStatusMutation.isPending ? (
                                                    <>
                                                        <Loader2 className="mr-2 h-4 w-4 animate-spin shrink-0" /> Mengecek...
                                                    </>
                                                ) : (
                                                    <>
                                                        <RefreshCw className="mr-2 h-4 w-4 shrink-0" /> Cek Status
                                                    </>
                                                )}
                                            </Button>
                                        )}
                                        <Button
                                            onClick={() => navigate("/order/pembayaran")}
                                            className={cn(
                                                "flex-1 font-bold text-white text-xs uppercase tracking-wider rounded-xl px-4 py-2.5 transition-all shadow-md",
                                                payment.status === "paid" ? "bg-emerald-600 hover:bg-emerald-700 shadow-emerald-600/20" :
                                                    "bg-blue-600 hover:bg-blue-700 shadow-blue-600/20"
                                            )}
                                        >
                                            {payment.status === "paid" ? "Selesai" : "Lihat Detail"}
                                        </Button>
                                    </div>
                                </div>

                                {/* Info biaya admin VA - hanya relevan selama tagihan belum lunas */}
                                {payment.status !== "paid" && (
                                    <PaymentFeeNotice
                                        amount={payment.amount}
                                        isUsd={payment.gateway_transaction_id === "2"}
                                        className="mt-6 pt-6 border-t border-slate-100 dark:border-slate-800"
                                    />
                                )}
                            </div>

                        </div>
                    </CardContent>
                </Card>
            ) : null}

            {/* 2. Bento Grid Section */}
            <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-6 gap-6">
                    {features.map((item) => {
                        const isPrimaryAction = item.key === "order" || item.key === "verification";

                        // Fitur verifikasi tidak boleh di-disable oleh activePeriod (hanya Order yang disabled)
                        const disabled = item.key === "order" && (isLoading || isOrderDisabled);

                        return (
                            <motion.div
                                key={item.key}
                                whileHover={!disabled ? { y: -5 } : {}}
                                onClick={!disabled ? item.action : undefined}
                                className={cn(
                                    "relative h-full cursor-pointer group",
                                    item.size === "large" ? "md:col-span-4" :
                                        item.size === "medium" ? "md:col-span-3" : "md:col-span-2",
                                    disabled && "cursor-not-allowed"
                                )}
                            >
                                {/* --- 1. ANIMATED BORDER GLOW --- */}
                                {isPrimaryAction && !disabled && (
                                    <div className="absolute -inset-[2px] bg-gradient-to-r from-blue-600 via-white to-blue-600 rounded-[2.1rem] blur-[2px] opacity-30 group-hover:opacity-100 group-hover:blur-[4px] transition duration-500 animate-gradient-x pointer-events-none z-0"
                                        style={{ backgroundSize: '200% 200%' }} />
                                )}

                                <Card className={cn(
                                    "relative z-10 h-full border-none shadow-sm rounded-[2rem] overflow-hidden bg-white dark:bg-slate-900 transition-all duration-500 flex flex-col justify-between",
                                    disabled ? "opacity-60 grayscale" : "hover:shadow-2xl hover:shadow-slate-200/50 dark:hover:shadow-none"
                                )}>

                                    {/* --- 2. INDIKATOR KELIP-KELIP --- */}
                                    {isPrimaryAction && !disabled && item.badge && (
                                        <div className="absolute top-6 right-6 z-20 flex items-center gap-2">
                                            <span className="text-[10px] font-black uppercase tracking-tighter text-blue-600 dark:text-blue-400 opacity-0 group-hover:opacity-100 transition-opacity">
                                                {item.badge}
                                            </span>
                                            <span className="relative flex h-3 w-3">
                                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                                                <span className="relative inline-flex rounded-full h-3 w-3 bg-blue-600"></span>
                                            </span>
                                        </div>
                                    )}

                                    {/* Glassmorphism Effect Overlay */}
                                    <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-slate-50/50 dark:to-slate-800/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                                    <div className="relative z-10 p-8 h-full flex flex-col justify-between">
                                        <div className="space-y-6">
                                            {/* Icon Header */}
                                            <div className={cn(
                                                "w-12 h-12 rounded-2xl flex items-center justify-center shadow-sm border border-white dark:border-slate-800",
                                                item.color === "blue" ? "bg-blue-50 text-blue-600 dark:bg-blue-900/30" :
                                                    item.color === "emerald" ? "bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30" :
                                                        item.color === "indigo" ? "bg-indigo-50 text-indigo-600 dark:bg-indigo-900/30" :
                                                            item.color === "amber" ? "bg-amber-50 text-amber-600 dark:bg-amber-900/30" :
                                                                "bg-sky-50 text-sky-600 dark:bg-sky-900/30"
                                            )}>
                                                {item.icon}
                                            </div>

                                            <div className="space-y-2">
                                                <CardTitle className="text-xl font-bold tracking-tight text-slate-900 dark:text-white">
                                                    {item.title}
                                                </CardTitle>
                                                <CardDescription className="text-sm font-medium leading-relaxed text-slate-500 dark:text-slate-400 line-clamp-3">
                                                    {item.description}
                                                </CardDescription>
                                            </div>
                                        </div>

                                        <div className="pt-8 flex items-center justify-between">
                                            <Button
                                                variant="ghost"
                                                className={cn(
                                                    "p-0 h-auto hover:bg-transparent text-xs font-black uppercase tracking-widest flex items-center gap-2 transition-all",
                                                    item.color === "blue" ? "text-blue-600" :
                                                        item.color === "emerald" ? "text-emerald-600" :
                                                            item.color === "indigo" ? "text-indigo-600" :
                                                                item.color === "amber" ? "text-amber-600" : "text-sky-600",
                                                    disabled && "text-slate-400"
                                                )}
                                            >
                                                <span className="flex items-center gap-2">
                                                    {disabled ? (
                                                        <><Lock className="w-3.5 h-3.5" /> {t('dashboard.locked_button')}</>
                                                    ) : (
                                                        <><TrendingUp className="w-3.5 h-3.5" /> {t('dashboard.action_button')}</>
                                                    )}
                                                </span>
                                            </Button>

                                            {!disabled && (
                                                <div
                                                    className="w-10 h-10 rounded-full bg-slate-50 dark:bg-slate-800 flex items-center justify-center text-slate-400 group-hover:bg-slate-900 group-hover:text-white dark:group-white dark:group-hover:text-slate-900 transition-all"
                                                >
                                                    <ChevronRight className="w-5 h-5" />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </Card>
                            </motion.div>
                        );
                    })}
                </div>
            </div>

            {/* Style inline tetap dipertahankan */}
            <style dangerouslySetInnerHTML={{
                __html: `
                @keyframes gradient-x {
                    0% { background-position: 0% 50%; }
                    50% { background-position: 100% 50%; }
                    100% { background-position: 0% 50%; }
                }
                .animate-gradient-x {
                    animation: gradient-x 3s ease infinite;
                }
            `}} />
        </div>
    );
}